import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowRight, FileText } from "lucide-react";
import { allTutorials } from "@/data/tutorials";

const escapeHtml = (s) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const highlight = (text, q) => {
  if (!q) return escapeHtml(text);
  const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig");
  return escapeHtml(text).replace(
    re,
    '<mark class="search-highlight">$1</mark>'
  );
};

const buildIndex = () =>
  allTutorials.map((t) => ({
    slug: t.slug,
    id: t.id,
    title: t.title,
    chapter: t.chapterTitle,
    intro: t.intro || "",
    steps: (t.steps || []).join(" \u2022 "),
    cmsPath: Object.values(t.meta || {}).join(" / "),
    accent: t.chapterAccent,
  }));

export const SearchOverlay = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const index = useMemo(buildIndex, []);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return index.slice(0, 8);
    return index
      .map((t) => {
        const hay = `${t.title} ${t.chapter} ${t.intro} ${t.steps} ${t.cmsPath}`.toLowerCase();
        const score =
          (t.title.toLowerCase().includes(term) ? 5 : 0) +
          (t.chapter.toLowerCase().includes(term) ? 2 : 0) +
          (hay.includes(term) ? 1 : 0);
        return { ...t, score };
      })
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 20);
  }, [q, index]);

  useEffect(() => {
    setActive(0);
  }, [q, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((i) => Math.min(i + 1, results.length - 1));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
      }
      if (e.key === "Enter") {
        const r = results[active];
        if (r) {
          navigate(`/tutorial/${r.slug}`);
          onClose();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, results, active, navigate]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center pt-[8vh] px-4 animate-fade-in"
      onClick={onClose}
      data-testid="search-overlay"
    >
      <div
        className="w-full max-w-2xl bg-background brut-border brut-shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b-2 border-foreground px-4 py-3">
          <Search className="w-6 h-6" strokeWidth={2.5} />
          <input
            autoFocus
            data-testid="search-input"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search tutorials, steps, CMS paths…"
            className="flex-1 bg-transparent outline-none text-xl placeholder:text-muted-foreground"
          />
          <button
            onClick={onClose}
            aria-label="Close search"
            data-testid="search-close-button"
            className="kbd hover:bg-brut-yellow"
          >
            esc
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {results.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <p className="font-display text-2xl font-extrabold">
                No matches.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Try “forex”, “banner”, “role”, or “footer”.
              </p>
            </div>
          ) : (
            <ul>
              {results.map((r, i) => (
                <li key={r.slug}>
                  <button
                    data-testid={`search-result-${r.slug}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => {
                      navigate(`/tutorial/${r.slug}`);
                      onClose();
                    }}
                    className={`w-full text-left px-4 py-3 border-b-2 border-foreground/10 flex items-start gap-3 transition-colors ${
                      i === active ? "bg-brut-yellow" : "hover:bg-muted"
                    }`}
                  >
                    <FileText
                      className="w-5 h-5 mt-0.5 flex-shrink-0"
                      strokeWidth={2.5}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="font-mono text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                          {r.id} · {r.chapter}
                        </span>
                      </div>
                      <p
                        className="font-semibold text-base mt-0.5 truncate"
                        dangerouslySetInnerHTML={{
                          __html: highlight(r.title, q),
                        }}
                      />
                      {q && (
                        <p
                          className="text-xs text-muted-foreground mt-1 line-clamp-2"
                          dangerouslySetInnerHTML={{
                            __html: highlight(
                              (r.intro + " " + r.steps).slice(0, 220),
                              q
                            ),
                          }}
                        />
                      )}
                    </div>
                    <ArrowRight
                      className="w-4 h-4 flex-shrink-0 mt-1"
                      strokeWidth={2.5}
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t-2 border-foreground px-4 py-2 bg-muted">
          <div className="flex items-center gap-3 text-[11px] font-mono text-muted-foreground">
            <span className="flex items-center gap-1">
              <kbd className="kbd">↑</kbd>
              <kbd className="kbd">↓</kbd> navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="kbd">↵</kbd> open
            </span>
          </div>
          <span className="text-[11px] font-mono text-muted-foreground">
            {results.length} result{results.length === 1 ? "" : "s"}
          </span>
        </div>
      </div>
    </div>
  );
};
